import type { Observable } from 'rxjs';
import type { CdnInfo, LandingPageUser, LandingPageUserTariff, MemberAreaUser } from '@common/types/user.type';
import type { UserState } from './user.reducer';

import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';

import type { ETariff, ETariffAbbr, Widgets } from '@common/enums';
import { EDU_FREE_TARIFF } from '@web/core/constants/tariff.const';

import * as UserSelectors from './user.selectors';
import * as UserActions from './user.actions';

@Injectable()
export class UserFacade {
    private readonly store = inject<Store<UserState>>(Store);

    public isEduTariffFree: boolean = true;
    public readonly user$: Observable<LandingPageUser> = this.store.select(UserSelectors.selectUser);
    public readonly userTariff$: Observable<LandingPageUserTariff> = this.store.select(UserSelectors.selectUserTariff);
    public readonly tariffVersion$: Observable<ETariff> = this.store.select(UserSelectors.selectTariffVersion);
    public readonly tariffAbbr$: Observable<ETariffAbbr> = this.store.select(UserSelectors.selectTariffAbbr);
    public readonly nextTariff$: Observable<ETariffAbbr> = this.store.select(UserSelectors.selectNextTariff);
    public readonly nextTariffClass$: Observable<string> = this.store.select(UserSelectors.selectNextTariffClass);
    public readonly isUserTariffFree$: Observable<boolean> = this.store.select(UserSelectors.selectIsUserTariffFree);
    public readonly isOffice$: Observable<boolean> = this.store.select(UserSelectors.selectIsOffice);
    public readonly userAvailableSiteCount$: Observable<number> = this.store.select(
        UserSelectors.selectUserAvailableSiteCount
    );
    public readonly userId$: Observable<number> = this.store.select(UserSelectors.selectUserId);
    public readonly eduUserTariff$: Observable<string> = this.store.select(UserSelectors.selectEduUserTariff);
    public readonly hasAccessToTab$: Observable<{ hasAccess: boolean; nextTariff: ETariffAbbr | null }> =
        this.store.select(UserSelectors.selectHasAccessToTab);
    public readonly userCdnInfo$: Observable<CdnInfo> = this.store.select(UserSelectors.selectCdnInfo);
    public readonly memberAreaUser$: Observable<MemberAreaUser> = this.store.select(UserSelectors.selectMemberAreaUser);
    public readonly memberAreaUserTimezone$: Observable<string> = this.store.select(
        UserSelectors.selectMemberAreaUserTimezone
    );
    public readonly memberAreaUserCreatedAt$: Observable<string> = this.store.select(
        UserSelectors.selectMemberAreaUserCreatedAt
    );

    constructor() {
        this.eduUserTariff$.subscribe((tariff) => (this.isEduTariffFree = tariff === EDU_FREE_TARIFF));
    }

    public hasAccessToElement(widgetName: Widgets): Observable<{ hasAccess: boolean; nextTariff: ETariffAbbr | null }> {
        return this.store.select(UserSelectors.hasAccessToElement(widgetName));
    }

    public setUserCdnInfo(cdnInfo: CdnInfo): void {
        this.store.dispatch(UserActions.setUserCdnInfo({ cdnInfo }));
    }
}
